/**
 * FacilityLens -- Main capture + analysis page.
 *
 * Steps:
 * 1. Capture photo (+ optional location)
 * 2. Analyzing spinner (polls scan status)
 * 3a. Needs more info -> follow-up photo prompt
 * 3b. Identified -> render FacilityLensResult
 */

import { useState, useRef, useEffect, Component, type ReactNode } from "react";
import { Button, Select, message } from "antd";
import {
	CameraOutlined,
	ArrowLeftOutlined,
	ScanOutlined,
	InfoCircleOutlined,
	EnvironmentOutlined,
	CheckOutlined,
	CloseOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { CORE_URL } from "@/constants/api";
import { VITE_CORE_API_URL } from "@/constants/env";
import { auth } from "@/services/auth-service";
import { uploadPhotoToFirebase } from "@/services/storage-service";
import {
	flCreateScan,
	flAddPhoto,
	flReAnalyze,
} from "@/services/facility-lens-service";
import { useFacilityLensStore } from "@/stores/facility-lens-store";
import FacilityLensResult from "./FacilityLensResult";
import "./FacilityLens.scss";

type Step = "capture" | "analyzing" | "needs-more" | "results";

// Error boundary to prevent white screens
class FacilityLensErrorBoundary extends Component<
	{ children: ReactNode },
	{ hasError: boolean; error: string }
> {
	state = { hasError: false, error: "" };

	static getDerivedStateFromError(error: Error) {
		return { hasError: true, error: error.message };
	}

	componentDidCatch(error: Error) {
		console.error("[FacilityLens] Render crash:", error);
	}

	render() {
		if (this.state.hasError) {
			return (
				<div style={{ textAlign: "center", padding: 60 }}>
					<h2>Something went wrong</h2>
					<p style={{ color: "#888" }}>{this.state.error}</p>
					<Button type="primary" onClick={() => window.location.href = "/forms"}>
						Back to Vision Capture
					</Button>
				</div>
			);
		}
		return this.props.children;
	}
}

const FacilityLensInner = () => {
	const navigate = useNavigate();
	const fileRef = useRef<HTMLInputElement>(null);
	const followUpRef = useRef<HTMLInputElement>(null);

	const {
		activeScan,
		setScan,
		setKnowledge,
		analyzing,
		setAnalyzing,
		reset,
	} = useFacilityLensStore();

	const [step, setStep] = useState<Step>("capture");
	const [photoFile, setPhotoFile] = useState<File | null>(null);
	const [photoPreview, setPhotoPreview] = useState<string | null>(null);
	const [uploading, setUploading] = useState(false);

	// Location state
	const [locations, setLocations] = useState<any[]>([]);
	const [loadingLocations, setLoadingLocations] = useState(true);
	const [selectedLocationId, setSelectedLocationId] = useState<number | undefined>();

	// Geolocation state
	const [geoDetectedLocation, setGeoDetectedLocation] = useState<any | null>(null);
	const [geoDistance, setGeoDistance] = useState<number | null>(null);
	const [geoDismissed, setGeoDismissed] = useState(false);
	const [geoDetecting, setGeoDetecting] = useState(false);

	// Reset store on mount
	useEffect(() => {
		reset();
		loadLocations();
		detectGeoLocation();
	}, []);

	const loadLocations = async () => {
		setLoadingLocations(true);
		try {
			const token = await auth.currentUser?.getIdToken();
			const res = await fetch(`${VITE_CORE_API_URL}${CORE_URL}/location`, {
				method: "GET",
				headers: { Authorization: `Bearer ${token}` },
			});
			if (!res.ok) throw new Error("Failed");
			const json = await res.json();
			setLocations(json.data || []);
		} catch {
			// Non-critical
		} finally {
			setLoadingLocations(false);
		}
	};

	// ─── Geolocation detection ───
	const detectGeoLocation = async () => {
		if (!navigator.geolocation) return;
		setGeoDetecting(true);
		try {
			const pos = await new Promise<GeolocationPosition>((resolve, reject) => {
				navigator.geolocation.getCurrentPosition(resolve, reject, {
					enableHighAccuracy: true,
					timeout: 8000,
					maximumAge: 60000,
				});
			});
			const { latitude, longitude } = pos.coords;

			const token = await auth.currentUser?.getIdToken();
			const res = await fetch(
				`${VITE_CORE_API_URL}${CORE_URL}/location/geo/search?latitude=${latitude}&longitude=${longitude}`,
				{ headers: { Authorization: `Bearer ${token}` } },
			);
			if (!res.ok) return;
			const json = await res.json();
			const nearby = json.data;
			if (Array.isArray(nearby) && nearby.length > 0) {
				const closest = nearby[0];
				setGeoDetectedLocation(closest);
				setGeoDistance(closest.distance_km ? Math.round(closest.distance_km * 1000) : null);
			}
		} catch {
			// GPS not available or denied -- silently fall back to manual selection
		} finally {
			setGeoDetecting(false);
		}
	};

	const applyGeoLocation = () => {
		if (geoDetectedLocation) {
			setSelectedLocationId(geoDetectedLocation.id);
			setGeoDismissed(true); // hide banner after applying
		}
	};

	const dismissGeoLocation = () => {
		setGeoDismissed(true);
	};

	// ─── Photo capture ───
	const handlePhotoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (!file) return;
		setPhotoFile(file);
		setPhotoPreview(URL.createObjectURL(file));
	};

	// ─── Compute SHA-256 hash of a File ───
	const hashFile = async (file: File): Promise<string> => {
		const buffer = await file.arrayBuffer();
		const hashBuffer = await crypto.subtle.digest("SHA-256", buffer);
		return Array.from(new Uint8Array(hashBuffer))
			.map((b) => b.toString(16).padStart(2, "0"))
			.join("");
	};

	// ─── Submit for analysis ───
	const handleAnalyze = async () => {
		if (!photoFile) return;
		setUploading(true);
		setStep("analyzing");
		setAnalyzing(true);
		try {
			// 1. Compute photo hash for dedup (runs in parallel with upload)
			const [photoHash, photoUrl] = await Promise.all([
				hashFile(photoFile),
				uploadPhotoToFirebase(photoFile, "facility-lens", `${Date.now()}_${photoFile.name}`),
			]);

			// 2. Create scan — backend checks hash for duplicates
			const scan = await flCreateScan(photoUrl, selectedLocationId, photoHash);
			setScan(scan);

			// 3. If the scan came back already identified (Tier 1: hash match), skip AI entirely
			if (scan.status === "IDENTIFIED" && scan.aiIdentification) {
				message.success("Instant match — we already have this on file!");
				if (scan.knowledge) setKnowledge(scan.knowledge);
				setStep("results");
				return;
			}

			// 4. Otherwise, run the tiered analysis pipeline (Tier 2 quick triage -> Tier 3 deep)
			const result = await flReAnalyze(scan.id);
			setScan(result);

			// 5. Handle result with tier-specific feedback
			if (result.status === "IDENTIFIED") {
				if (result.knowledge) setKnowledge(result.knowledge);
				const tier = result.resolvedTier;
				if (tier === "QUICK_TRIAGE_CACHE") {
					message.success("Fast match — recognized from our knowledge base!");
				} else if (tier === "FAST_ANALYSIS") {
					message.success("Asset identified!");
				} else {
					message.success("Deep analysis complete — asset identified!");
				}
				setStep("results");
			} else if (result.status === "NEEDS_MORE_INFO") {
				setStep("needs-more");
			} else {
				message.error("Analysis did not produce a result. Please try again.");
				setStep("capture");
			}
		} catch (err: any) {
			console.error("FacilityLens analysis error:", err);
			message.error(err.message || "Analysis failed. Please try again.");
			setStep("capture");
		} finally {
			setUploading(false);
			setAnalyzing(false);
		}
	};

	// ─── Follow-up photo ───
	const handleFollowUpPhoto = async (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (!file || !activeScan) return;

		setUploading(true);
		setStep("analyzing");
		setAnalyzing(true);
		try {
			const folder = "facility-lens";
			const filename = `${Date.now()}_followup_${file.name}`;
			const photoUrl = await uploadPhotoToFirebase(file, folder, filename);

			const photoType = activeScan.followUpRequest?.type ?? "GENERAL";
			await flAddPhoto(activeScan.id, photoUrl, photoType);

			// Re-analyze synchronously
			const result = await flReAnalyze(activeScan.id);
			setScan(result);

			if (result.status === "IDENTIFIED") {
				if (result.knowledge) setKnowledge(result.knowledge);
				setStep("results");
			} else if (result.status === "NEEDS_MORE_INFO") {
				setStep("needs-more");
			} else {
				message.error("Analysis did not produce a result. Please try again.");
				setStep("capture");
			}
		} catch (err: any) {
			message.error(err.message || "Failed to add photo");
			setStep("needs-more");
		} finally {
			setUploading(false);
			setAnalyzing(false);
		}
	};

	// ─── Start over ───
	const handleStartOver = () => {
		reset();
		setStep("capture");
		setPhotoFile(null);
		setPhotoPreview(null);
		setSelectedLocationId(undefined);
	};

	return (
		<div className="fl-page">
			{/* Header */}
			<div className="fl-header">
				<Button
					type="text"
					icon={<ArrowLeftOutlined />}
					onClick={() => navigate("/forms")}
					className="fl-back"
				>
					Vision Capture
				</Button>
				<h1>
					<ScanOutlined className="fl-title-icon" /> FacilityLens
				</h1>
				<p className="fl-subtitle">
					AI-powered asset identification and diagnosis
				</p>
			</div>

			{/* ─── Step 1: Capture ─── */}
			{step === "capture" && (
				<div className="fl-capture-step">
					{!photoPreview ? (
						<div className="fl-capture-prompt">
							<div className="fl-camera-circle">
								<CameraOutlined />
							</div>
							<h2>Take a Photo of the Asset</h2>
							<p>
								Point your camera at any equipment or asset. FacilityLens will
								identify it and provide diagnostic information.
							</p>
							<Button
								type="primary"
								size="large"
								icon={<CameraOutlined />}
								onClick={() => fileRef.current?.click()}
							>
								Open Camera
							</Button>
							<input
								ref={fileRef}
								type="file"
								accept="image/*"
								capture="environment"
								style={{ display: "none" }}
								onChange={handlePhotoSelect}
							/>
						</div>
					) : (
						<div className="fl-capture-review">
							<div className="fl-photo-preview">
								<img src={photoPreview} alt="Captured asset" />
							</div>

						{/* Geolocation banner */}
						{geoDetectedLocation && !geoDismissed && (
							<div className="fl-geo-banner">
								<EnvironmentOutlined className="fl-geo-icon" />
								<div className="fl-geo-text">
									<strong>{geoDetectedLocation.name || geoDetectedLocation.address1}</strong>
									{geoDistance !== null && (
										<span className="fl-geo-distance">
											{geoDistance < 1000 ? `${geoDistance}m away` : `${(geoDistance / 1000).toFixed(1)}km away`}
										</span>
									)}
								</div>
								<Button
									type="primary"
									size="small"
									icon={<CheckOutlined />}
									onClick={applyGeoLocation}
								>
									Apply
								</Button>
								<Button
									type="text"
									size="small"
									icon={<CloseOutlined />}
									onClick={dismissGeoLocation}
									className="fl-geo-dismiss"
								/>
							</div>
						)}
						{geoDetecting && !geoDetectedLocation && (
							<div className="fl-geo-detecting">
								<EnvironmentOutlined /> Detecting your location...
							</div>
						)}

						<div className="fl-location-select">
							<label>Location {geoDetectedLocation ? "" : "(optional)"}</label>
							<Select
								showSearch
								placeholder="Select location..."
								optionFilterProp="label"
								allowClear
								value={selectedLocationId}
								onChange={setSelectedLocationId}
								loading={loadingLocations}
								style={{ width: "100%" }}
								options={locations.map((l: any) => ({
									value: l.id,
									label: l.name || l.address1 || l.address || `Location #${l.id}`,
								}))}
							/>
						</div>

							<div className="fl-capture-actions">
								<Button
									size="large"
									onClick={() => {
										setPhotoFile(null);
										setPhotoPreview(null);
									}}
								>
									Retake Photo
								</Button>
								<Button
									type="primary"
									size="large"
									icon={<ScanOutlined />}
									onClick={handleAnalyze}
									loading={uploading}
								>
									Analyze with FacilityLens
								</Button>
							</div>
						</div>
					)}
				</div>
			)}

			{/* ─── Step 2: Analyzing ─── */}
			{step === "analyzing" && (
				<div className="fl-analyzing-step">
					{/* Photo with spinner orbiting behind it */}
					<div className="fl-analyzing-hero">
						<div className="fl-pulse-spinner">
							<div className="fl-pulse-core" />
							<div className="fl-pulse-ring fl-pulse-ring-1" />
							<div className="fl-pulse-ring fl-pulse-ring-2" />
							<div className="fl-pulse-ring fl-pulse-ring-3" />
							<div className="fl-pulse-glow" />
						</div>
						{photoPreview && (
							<div className="fl-analyzing-photo">
								<img src={photoPreview} alt="Being analyzed" />
							</div>
						)}
					</div>
					<h2>FacilityLens is analyzing...</h2>
					<p>
						Identifying the asset type, manufacturer, and gathering
						diagnostic information. This may take a few seconds.
					</p>
				</div>
			)}

			{/* ─── Step 3a: Needs More Info ─── */}
			{step === "needs-more" && activeScan?.followUpRequest && (
				<div className="fl-needsmore-step">
					<div className="fl-needsmore-icon">
						<InfoCircleOutlined />
					</div>
					<h2>We Need a Better Look</h2>
					<p className="fl-needsmore-instruction">
						{activeScan.followUpRequest.instruction}
					</p>
					{activeScan.followUpRequest.hint && (
						<div className="fl-needsmore-hint">
							<strong>Tip:</strong> {activeScan.followUpRequest.hint}
						</div>
					)}

					{activeScan.aiIdentification && (
						<div className="fl-partial-id">
							<span className="fl-partial-label">Initial identification:</span>
							<span className="fl-partial-value">
								{activeScan.aiIdentification.assetType}
								{activeScan.aiIdentification.confidence > 0 &&
									` (${activeScan.aiIdentification.confidence}% confidence)`}
							</span>
						</div>
					)}

					<Button
						type="primary"
						size="large"
						icon={<CameraOutlined />}
						onClick={() => followUpRef.current?.click()}
						loading={uploading}
					>
						Take Follow-Up Photo
					</Button>
					<input
						ref={followUpRef}
						type="file"
						accept="image/*"
						capture="environment"
						style={{ display: "none" }}
						onChange={handleFollowUpPhoto}
					/>

					<Button
						type="text"
						onClick={handleStartOver}
						className="fl-start-over"
					>
						Start Over
					</Button>
				</div>
			)}

			{/* ─── Step 3b: Results ─── */}
			{step === "results" && activeScan && (
				<FacilityLensResult
					scan={activeScan}
					onStartOver={handleStartOver}
				/>
			)}
		</div>
	);
};

const FacilityLens = () => (
	<FacilityLensErrorBoundary>
		<FacilityLensInner />
	</FacilityLensErrorBoundary>
);

export default FacilityLens;
